<template>
  <v-input
    :id="field.id"
    :error-messages="errors"
    :focused="focused"
    :hide-details="dense"
    :rules="rules"
    validate-on="input"
    :validation-value="modelValue"
  >
    <template #default="{ isValid }">
      <div
        class="v-field d-block"
        :class="{ 'v-field--error': isValid.value === false }"
      >
        <v-label v-if="!dense">
          <FieldLabel :field="field" />
        </v-label>

        <v-row dense>
          <v-col cols="6">
            <VTextField
              v-model="innerLat"
              :dense="dense"
              :disabled="disabled"
              :error="!isValid"
              :hide-details="dense"
              :rules="latRules"
              type="number"
              @update:focused="updateFocus('lat', $event)"
            >
              <template
                v-if="dense"
                #label
              >
                <FieldLabel :field="innerLatField" />
              </template>
            </VTextField>
          </v-col>

          <v-col cols="6">
            <VTextField
              v-model="innerLng"
              :dense="dense"
              :disabled="disabled"
              :error="!isValid"
              :hide-details="dense"
              :rules="lngRules"
              type="number"
              @update:focused="updateFocus('lng', $event)"
            >
              <template
                v-if="dense"
                #label
              >
                <FieldLabel :field="innerLngField" />
              </template>
            </VTextField>
          </v-col>
        </v-row>

        <v-row>
          <MapField
            v-if="position.lat() !== 0 && position.lng() !== 0 && centerPlacemark"
            :center="position"
            :placemarks="[centerPlacemark]"
            :zoom="14"
          />
        </v-row>
      </div>
    </template>
  </v-input>
</template>

<script setup lang="ts">
import { VTextField } from 'vuetify/components';
import { watchDebounced } from '@vueuse/core';
import FieldLabel from './FieldLabel.vue';
import { useMaps } from '@/composables/useMaps';

const props = defineProps<{
  field: FieldConfig
  rules?: FormRule[]
  errors?: string[]
  dense?: boolean
  modelValue: string | undefined
}>();
const emit = defineEmits(['update:modelValue']);

const innerLatFocused = ref(false);
const innerLngFocused = ref(false);
const focused = computed(() => {
  return innerLatFocused.value || innerLngFocused.value;
});

const disabled = computed(() => (!!props.field.readonly));

const innerLatField = ref<FieldConfig>({ id: 'latitude', label: 'Latitude', required: props.field.required, readonly: false, type: 'number', maxValue: 90, minValue: -90 });
const innerLngField = ref<FieldConfig>({ id: 'longitude', label: 'Longitude', required: props.field.required, readonly: false, type: 'number', maxValue: 180, minValue: -180 });

const latRules = [
  (v: number) => (v && v <= 90) || 'Latitude must be less than 90.',
  (v: number) => (v && v >= -90) || 'Latitude must be more than -90.',
];
const lngRules = [
  (v: number) => (v && v <= 180) || 'Longitude must be less than 180.',
  (v: number) => (v && v >= -180) || 'Longitude must be more than -180.',
];

// Initialize the Lat/Lng values
const innerLat = ref<number>();
const innerLng = ref<number>();
[innerLat.value, innerLng.value] = getLatLngFromString(props.modelValue);

// Configure map references
const { googleMapsEmbedApiKey } = useRuntimeConfig().public;
const { AdvancedMarkerElement, createFirePin } = await useMaps(googleMapsEmbedApiKey);

const position = ref<google.maps.LatLng>(new google.maps.LatLng(innerLat.value, innerLng.value));
const centerPlacemark = ref<google.maps.marker.AdvancedMarkerElement>();

// Configure and attach the incident placemark
centerPlacemark.value = new AdvancedMarkerElement({
  position: position.value,
  content: createFirePin().element,
  title: 'Incident location',
  gmpClickable: true,
});

// Update the inner fields whenever the model changes
watch(() => props.modelValue, val => {
  [innerLat.value, innerLng.value] = getLatLngFromString(val);
});

// Update the model whenever the inner fields change
watch([innerLat, innerLng], ([lat, lng]) => {
  emit('update:modelValue', `${lat},${lng}`);
});

// Update the model whenever the inner fields change
watchDebounced(
  () => [innerLat.value, innerLng.value],
  () => {
    if (innerLat.value && innerLng.value) {
      const newPosition = new google.maps.LatLng(innerLat.value, innerLng.value);
      const pin = createFirePin();

      centerPlacemark.value = new AdvancedMarkerElement({
        position: newPosition,
        content: pin.element,
        title: 'Incident location',
        gmpClickable: true,
      });

      position.value = newPosition;
    }
  },
  { debounce: 500, maxWait: 1000 },
);

/**
 * Updates the `focused` ref for one of the two inner fields
 *
 * @param field
 * @param value
 */
const updateFocus = (field: 'lat' | 'lng', value: boolean) => {
  if (field === 'lat') {
    innerLatFocused.value = value;
  }
  else {
    innerLngFocused.value = value;
  }
};
</script>

<style scoped lang="scss">
.v-label {
  margin-inline-start: 8px;
}
</style>
